import { Platform } from 'react-native';
import {
  requestMobileAttendanceCapture,
  assertMobileAttendanceCaptureActive,
  closeMobileAttendanceCapture,
} from './mobileAttendanceCapture';
import { getKioskAccessToken } from './kioskTokenStore';
import { browserAttendanceImage } from '../utils/browserAttendanceImage';
import { deleteTemporaryImage } from '../utils/temporaryImage';
import { apiClient } from '../api/apiClient';
import { API_ENDPOINTS } from '../api/apiEndpoints';
import type { FaceVerificationLogResult } from '../types/faceAttendance';

type Envelope<T> = { success: boolean; message: string; data: T };
type RecordValue = Record<string, unknown>;

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function textOrNull(value: unknown): string | null {
  return typeof value === 'string' && value.trim() ? value.trim() : null;
}

export type LogFaceVerificationInput = {
  employeeId: number;
  registrationId: number;
  verificationType: string;
  faceDetected: boolean;
  livenessVerified: boolean;
  identityMatched: boolean;
  confidenceScore: number;
  configuredThreshold: number;
  employeeFaceId?: number | null;
  deviceId?: number | null;
  kioskId?: number | null;
  locationId?: number | null;
  failureCode?: string | null;
  failureReason?: string | null;
  requestDurationMs?: number | null;
};

/**
 * Manual verification log (FACE_VERIFY).
 * Face-punch logs verification server-side; use this after identify-only.
 * Never send provider person/face IDs from the client.
 */
export async function logFaceVerification(
  input: LogFaceVerificationInput,
): Promise<FaceVerificationLogResult> {
  const body = {
    employeeId: input.employeeId,
    registrationId: input.registrationId,
    verificationType: input.verificationType,
    faceDetected: input.faceDetected,
    livenessVerified: input.livenessVerified,
    identityMatched: input.identityMatched,
    confidenceScore: input.confidenceScore,
    configuredThreshold: input.configuredThreshold,
    ...(input.employeeFaceId != null
      ? { employeeFaceId: input.employeeFaceId }
      : {}),
    ...(input.deviceId != null ? { deviceId: input.deviceId } : {}),
    ...(input.kioskId != null ? { kioskId: input.kioskId } : {}),
    ...(input.locationId != null ? { locationId: input.locationId } : {}),
    ...(input.failureCode ? { failureCode: input.failureCode } : {}),
    ...(input.failureReason ? { failureReason: input.failureReason } : {}),
    ...(input.requestDurationMs != null
      ? { requestDurationMs: input.requestDurationMs }
      : {}),
  };

  const response = await apiClient.post<Envelope<RecordValue>>(
    API_ENDPOINTS.FACE.VERIFICATIONS,
    body,
  );
  const data = response.data.data ?? {};
  const verificationLogId = numberOrNull(
    data.verificationLogId ?? data.faceVerificationLogId,
  );
  if (!verificationLogId) {
    throw Object.assign(new Error('Verification log response was incomplete'), {
      code: 'VERIFICATION_LOG_FAILED',
    });
  }

  return {
    verificationLogId,
    verificationLogUuid: textOrNull(data.verificationLogUuid),
    verificationSuccessful:
      data.verificationSuccessful === true || data.verificationSuccessful === 1,
    confidenceScore: numberOrNull(data.confidenceScore),
    failureCode: textOrNull(data.failureCode),
    failureReason: textOrNull(data.failureReason),
  };
}

let attendanceCaptureInProgress = false;
type CapturedImage = { appendTo: (form: FormData) => void };
type ImageSource = (body: Record<string, unknown>) => Promise<CapturedImage>;
let sharedImageSource: ImageSource | null = null;

function formWithBody(body: Record<string, unknown>): FormData {
  const form = new FormData();
  for (const [key, value] of Object.entries(body)) {
    if (value !== null && value !== undefined) form.append(key, String(value));
  }
  return form;
}

async function verifyCapture(form: FormData): Promise<void> {
  const verification = await apiClient.post<
    Envelope<{
      verified?: boolean;
      failureCode?: string;
      failureReason?: string;
    }>
  >(API_ENDPOINTS.ATTENDANCE.VERIFY_FACE, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  if (
    verification.data.success !== true ||
    verification.data.data?.verified !== true
  ) {
    throw Object.assign(
      new Error(
        verification.data.data?.failureReason ||
          verification.data.message ||
          'Face verification was not successful.',
      ),
      {
        code: verification.data.data?.failureCode || 'FACE_VERIFICATION_FAILED',
      },
    );
  }
}

/**
 * Opens the in-app camera at most once, verifies that capture, and hands the
 * same image to every action in `run`. Each action endpoint still verifies it.
 */
async function runCaptureSession<T>(
  run: (imageSource: ImageSource) => Promise<T>,
): Promise<T> {
  if (attendanceCaptureInProgress) {
    throw Object.assign(
      new Error('Face verification is already in progress.'),
      {
        code: 'FACE_VERIFICATION_IN_PROGRESS',
      },
    );
  }
  attendanceCaptureInProgress = true;
  let imageUri: string | undefined;
  let image: Promise<CapturedImage> | null = null;
  const capture = async (
    body: Record<string, unknown>,
  ): Promise<CapturedImage> => {
    const asset = await requestMobileAttendanceCapture();
    imageUri = asset.uri;
    assertMobileAttendanceCaptureActive();
    const fileName = asset?.fileName || 'attendance.jpg';
    let captured: CapturedImage;
    if (Platform.OS === 'web') {
      // A browser Blob can be left unreadable after the verification upload.
      // Copy the bytes and give the attendance action its own slice.
      captured = await browserAttendanceImage({
        uri: asset.uri,
        fileName,
        mimeType: asset.mimeType || 'image/jpeg',
      });
    } else {
      captured = {
        appendTo: form =>
          form.append('image', {
            uri: asset.uri,
            name: fileName,
            type: asset.mimeType || 'image/jpeg',
          } as unknown as Blob),
      };
    }
    const form = formWithBody(body);
    captured.appendTo(form);
    await verifyCapture(form);
    return captured;
  };
  try {
    return await run(body => (image ??= capture(body)));
  } finally {
    closeMobileAttendanceCapture();
    attendanceCaptureInProgress = false;
    if (imageUri) {
      if (Platform.OS === 'web') {
        if (imageUri.startsWith('blob:')) URL.revokeObjectURL(imageUri);
      } else {
        await deleteTemporaryImage(imageUri);
      }
    }
  }
}

function actionPayload(data: unknown): RecordValue {
  return data && typeof data === 'object' ? (data as RecordValue) : {};
}

async function postWithCapture(
  imageSource: ImageSource,
  endpoint: string,
  body: Record<string, unknown>,
): Promise<RecordValue> {
  const captured = await imageSource(body);
  assertMobileAttendanceCaptureActive();
  const form = formWithBody(body);
  captured.appendTo(form);
  // Send the capture again, never a client assertion or reusable log ID as proof.
  const response = await apiClient.post<Envelope<RecordValue>>(endpoint, form, {
    headers: { 'Content-Type': 'multipart/form-data' },
  });
  return actionPayload(response.data?.data);
}

/** Verify the capture before requesting an action; the action endpoint also
 * independently verifies the capture. Kiosk sessions retain
 * their existing server-verified continuation flow.
 */
export async function submitFaceVerifiedAttendance(
  endpoint: string,
  body: Record<string, unknown>,
): Promise<RecordValue> {
  if (getKioskAccessToken()) {
    const response = await apiClient.post<Envelope<RecordValue>>(endpoint, body);
    return actionPayload(response.data?.data);
  }
  if (sharedImageSource) {
    return postWithCapture(sharedImageSource, endpoint, body);
  }
  return runCaptureSession(imageSource =>
    postWithCapture(imageSource, endpoint, body),
  );
}

/**
 * Runs several attendance actions (e.g. missed break + check-out) from one
 * in-app face scan instead of opening the camera for each action.
 */
export async function withSingleFaceCapture<T>(
  run: () => Promise<T>,
): Promise<T> {
  if (getKioskAccessToken()) return run();
  return runCaptureSession(async imageSource => {
    sharedImageSource = imageSource;
    try {
      return await run();
    } finally {
      sharedImageSource = null;
    }
  });
}
